/**
 * Une requête de widget IDENTIQUE ne part qu'une fois par cycle de rendu.
 *
 * Chaque changement déclenche DEUX effets de `containers/Widget` dans le même cycle : l'effet
 * global sur `[widgetInfoStableKey, fieldsData, eventFilters, valueFilterSelected,
 * comparisonValues]` ET l'effet dédié au changement (filtres, comparaison, données du widget).
 * Les deux construisent le même corps : le serveur recevait chaque requête en double. Mesuré en
 * production (07/10/2026) : rafale médiane de 60 requêtes pour les 31 widgets de Missions.
 *
 * Pourquoi dédoublonner ici plutôt que retirer un effet : ils ne passent pas le même type de
 * changement à `isWidgetNeedToReload` (`filterChange` exclut les calendriers, `dataChange` non).
 * Retirer l'un ferait perdre en silence un rechargement que seul l'autre couvre. Ici, le premier
 * qui part gagne, et une requête DIFFÉRENTE dans le même cycle part toujours.
 *
 * ⚠️ Le « cycle » est la tâche JavaScript en cours : la mémoire est vidée par une micro-tâche, donc
 * avant tout nouvel événement (clic, réponse réseau, recalcul du cube). Un rechargement voulu plus
 * tard, même à corps identique (`dataRefreshToken`), part donc normalement.
 *
 * Module volontairement LÉGER, sans import : testable sans charger le graphe des widgets.
 */

/** Clé d'une requête : ce qui part au serveur, et le widget qui la recevra. */
export const cleRequeteWidget = (widgetId, corps) => `${widgetId}::${JSON.stringify(corps ?? null)}`

/**
 * Rend `dejaDemandee(cle)` : `false` à la première demande d'une clé dans le cycle (et la
 * retient), `true` aux suivantes.
 *
 * @param {(f: () => void) => void} planifier  vide la mémoire après le cycle — injectable pour les tests
 */
export const creerFiltreRequetesIdentiques = (planifier = f => queueMicrotask(f)) => {
  let demandees = null
  return cle => {
    if (demandees === null) {
      demandees = new Set()
      planifier(() => {
        demandees = null
      })
    }
    if (demandees.has(cle)) return true
    demandees.add(cle)
    return false
  }
}
